import React, { createContext, useState, useEffect, useContext } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as api from '../api/admin';

export const AdminContext = createContext({});
export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      console.log('Checking admin authentication state...');
      const token = await AsyncStorage.getItem('adminAuthToken');
      const adminData = await AsyncStorage.getItem('adminUser');
      
      if (token && adminData) {
        const parsedAdmin = JSON.parse(adminData);
        setAdmin(parsedAdmin);
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      // Clear any existing auth data
      await AsyncStorage.multiRemove(['adminAuthToken', 'adminUser']);
      
      const response = await api.loginAdmin(email, password);
      
      if (!response.token || !response.admin) {
        throw new Error('Invalid response from server');
      }

      // Store auth data
      await AsyncStorage.multiSet([
        ['adminAuthToken', response.token],
        ['adminUser', JSON.stringify(response.admin)]
      ]);

      setAdmin(response.admin);
      return response;
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status
      });

      if (error.message === 'Network Error') {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (error.response?.status === 401) {
        setError('Invalid email or password');
      } else if (error.response?.status === 500) {
        setError('Server error occurred. Please try again later.');
      } else if (error.message === 'Invalid server response') {
        setError('Received invalid response from server. Please try again.');
      } else {
        setError(error.message || 'An unexpected error occurred. Please try again.');
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('AdminContext: Starting logout');
      
      // Verify we have the items to clear
      const token = await AsyncStorage.getItem('adminAuthToken');
      const adminData = await AsyncStorage.getItem('adminUser');
      
      console.log('AdminContext: Current token exists:', !!token);
      console.log('AdminContext: Current admin data exists:', !!adminData);

      // Clear auth data from storage
      await AsyncStorage.multiRemove(['adminAuthToken', 'adminUser']);
      
      // Verify items were cleared
      const tokenAfter = await AsyncStorage.getItem('adminAuthToken');
      const adminDataAfter = await AsyncStorage.getItem('adminUser');
      
      console.log('AdminContext: Token cleared:', !tokenAfter);
      console.log('AdminContext: Admin data cleared:', !adminDataAfter);

      // Clear state
      setAdmin(null);
      console.log('AdminContext: Admin state cleared, new state:', null);

      return { success: true };
    } catch (error) {
      console.error('AdminContext: Logout error:', error);
      throw error;
    }
  };

  const clearError = () => setError(null);

  return (
    <AdminContext.Provider
      value={{
        admin,
        loading,
        error,
        login,
        logout,
        clearError,
        isAuthenticated: !!admin
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
